import { Dialog } from '@material-ui/core';
import React, { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { withRouter } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';
import { replaceHistory } from '../common/history-replace';

import { Transition } from '../core-ui/dialog-transition';
import { AgentContext, AgentContextValue } from './use-agent';
import { usePrevious } from './use-previous';

interface PageDialogProviderProps {
    showDialog: (content: JSX.Element, url?: string, mode?: 'stack' | 'replace') => void;
    onClose: (() => void) | undefined;
    registerAgentContext: (context: AgentContextValue) => void;
}

const PageDialogContext = createContext<PageDialogProviderProps>({
    onClose: undefined,
    registerAgentContext: undefined,
    showDialog: undefined
});

type PageDialogProviderComponentProps = RouteComponentProps & { children?: ReactNode };

interface DialogStackItem {
    content: JSX.Element;
    url?: string;
}

const TRANSITION_DURATION_MS = 50;

const PageDialogProviderComponent = ({ children, history }: PageDialogProviderComponentProps): JSX.Element => {
    const [dialogStack, setDialogStack] = useState<DialogStackItem[]>([]);
    const [baseUrl, setBaseUrl] = useState<string>(undefined);
    const [agentContext, setAgentContext] = useState<AgentContextValue | null>(null);
    const previousLocation = usePrevious(history.location.pathname);

    useEffect(() => {
        if (previousLocation !== undefined && previousLocation !== history.location.pathname) {
            setDialogStack([]);
            setBaseUrl(undefined);
        }
    }, [history.location.pathname]);

    const showDialog = (content: JSX.Element, url?: string, mode: 'stack' | 'replace' = 'stack') => {
        if (mode === 'replace') {
            // If there are existing dialogs, close them first
            if (dialogStack.length > 0) {
                setDialogStack([]);
                // Wait for the exit animation to complete before opening the new dialog
                setTimeout(() => {
                    setDialogStack([{ content, url }]);
                    if (url) {
                        replaceHistory({}, '', url);
                    }
                }, TRANSITION_DURATION_MS); // Match this with the transition duration (usually 225ms for Material UI Slide)
                return;
            }
        }

        if (dialogStack.length === 0) {
            setBaseUrl(window.location.pathname);
        }

        setDialogStack([...dialogStack, { content, url }]);
        if (url) {
            replaceHistory({}, '', url);
        }
    };

    const registerAgentContext = React.useCallback((context: AgentContextValue) => {
        setAgentContext(context);
    }, []);

    const handleClose = React.useCallback(() => {
        const newStack = [...dialogStack];
        newStack.pop();
        setDialogStack(newStack);

        // Find the last dialog with a URL
        let foundUrl = false;
        for (let i = newStack.length - 1; i >= 0; i--) {
            if (newStack[i].url) {
                replaceHistory({}, '', newStack[i].url);
                foundUrl = true;
                break;
            }
        }

        // If no dialog has a URL, revert to base URL
        if (!foundUrl) {
            replaceHistory({}, '', baseUrl);
        }
    }, [dialogStack, baseUrl]);

    const dialogs = dialogStack.map((dialog, index) => (
        <Dialog
            key={index}
            fullScreen={true}
            open={true}
            onClose={handleClose}
            TransitionComponent={Transition}
            disableEnforceFocus={true}
        >
            {dialog.content}
        </Dialog>
    ));

    const onClose = dialogStack.length > 0 ? handleClose : undefined;

    return (
        <PageDialogContext.Provider value={{ showDialog, onClose, registerAgentContext }}>
            <AgentContext.Provider value={agentContext}>
                {children}
                {dialogs}
            </AgentContext.Provider>
        </PageDialogContext.Provider>
    );
};

const PageDialogProvider = withRouter(PageDialogProviderComponent);

const usePageDialog = () => useContext(PageDialogContext);

export { PageDialogProvider, usePageDialog };
